import React, { useEffect, useCallback, Suspense, lazy } from 'react';
import { BrowserRouter as Router, Route, Redirect, Switch } from 'react-router-dom';
import { connect, useDispatch } from 'react-redux';
import LogRocket from 'logrocket';
import Api from './util/Api';
import Config from './util/Config';
import { setUser } from './reducers/user/actions';

// Components
import Loading from './components/Loading';
import Confirm from './components/Confirm';
import RouteFallback from './components/RouteFallback';

// Pages
//
// Split at the authentication boundary. `Home` is the entire authenticated application —
// the offering dashboard, comments, documents and settings, plus every chart and table
// they pull in. Importing it statically meant a logged-out visitor on /signin downloaded
// all of it before the login form could render, and a logged-in user downloaded the
// sign-in view they will never see.
//
// Both sides are `lazy` so each visitor fetches only the half they are actually in. The
// build previously emitted ONE bundle (`main.js`, 293.88 kB gzipped) with no route
// splitting at all.
const Signin = lazy(() => import('./views/Signin/Signin'));
const Home = lazy(() => import('./views/Home'));

// The notification stack is mounted for the whole session but renders nothing until a
// notification is dispatched, and it is the only eager consumer of MUI. Loading it lazily
// keeps Alert/Grow/Portal and the emotion styling engine out of the initial bundle.
const Notify = lazy(() => import('./notifications/Notify'));

// AlreadySignedIn: if no token is found, show the component; otherwise, redirect to "/app"
function AlreadySignedIn({ component: Component, ...rest }) {
  const renderAlreadySignedIn = useCallback(
    (routeProps) =>
      !localStorage.getItem('token') ? <Component {...routeProps} /> : <Redirect to="/app" />,
    [Component]
  );
  return <Route {...rest} render={renderAlreadySignedIn} />;
}

// PrivateRoute: if token is found, render the component; otherwise, redirect to "/signin"
function PrivateRoute({ component: Component, ...rest }) {
  const renderPrivate = useCallback(
    (routeProps) =>
      localStorage.getItem('token') ? (
        <Component {...routeProps} />
      ) : (
        <Redirect to={{ pathname: '/signin', state: routeProps.location.pathname }} />
      ),
    [Component]
  );
  return <Route {...rest} render={renderPrivate} />;
}

function App() {
  const dispatch = useDispatch();

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      Api.setInstance(token);
    }
    const name = localStorage.getItem('name');
    const avatar = localStorage.getItem('avatar');
    if (name) {
      dispatch(setUser({ name, avatar }));
    }
    if (Config.LOGROCKET_SERVICE !== '') {
      LogRocket.init(Config.LOGROCKET_SERVICE);
    }
  }, [dispatch]);

  const renderNoMatch = useCallback(
    (routeProps) => (
      <div>
        No match for <code>{routeProps.location.pathname}</code>
      </div>
    ),
    []
  );

  return (
    <Router>
      <div>
        <Confirm />
        <Suspense fallback={null}>
          <Notify />
        </Suspense>
        <Loading />
        {/*
          Suspense wraps the whole Switch rather than each route: the `Redirect` entries
          must stay inside the same Switch to keep matching order, and a single boundary
          means one fallback rather than one per branch.
        */}
        <Suspense fallback={<RouteFallback />}>
          <Switch>
            <AlreadySignedIn path="/signin" component={Signin} />
            <Redirect exact from="/app/company" to="/app" />
            <Redirect exact from="/app/about" to="/app" />
            <PrivateRoute path="/app" component={Home} />
            <Redirect exact from="/" to="/app" />
            <Route render={renderNoMatch} />
          </Switch>
        </Suspense>
      </div>
    </Router>
  );
}

export default connect()(App);
